/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ButtonSelect,
  Icon,
  Menu,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { COL_FILTERS_MODEL } from '../../../models/CertsModels';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  GROUP_BY_APP_VALUE,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import SortFilter from '../../Common/SortFilter';

import { CERT_ENTITLEMENT_VALUEKEY, FIXED_COL_FILTERS } from './constants';
import { invertedMessages } from './messages';
import {
  checkParentUserExist,
  getColumnNameIdOrWarnMessage,
  getEOColumnNameIdOrWarnMessage,
  getEORowSelectionName,
  getRowSelectionName,
} from './utils';

import './DynamicSort.css';

function DynamicSort({
  rowFilters,
  colFilters,
  handleSort,
  resetDynamicSort,
  appliedRowFilters,
  isInMore,
  isButtonKindIcon,
}) {
  const intl = useIntl();

  const [options, setOptions] = useState([]);
  const [columnName, setColumnName] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const { messages } = useSelector((state) => state?.user);
  const {
    core: { isLoading, campaignType },
    tabs: { tabData },
    viewControls: {
      groupByOptions,
      dynamicSortFilter: { sortByColumn, sortDirection: sortOrder },
    },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize(invertedMessages);

  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isSAOrAOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_APPLICATION_OWNER ||
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  // filter active row filters , checking group by selection
  const activeRowFilters = appliedRowFilters.filter((rowValue) => {
    if (isUsingGroupBy) {
      return rowValue !== CERT_ENTITLEMENT_VALUEKEY?.USER;
    }

    return true;
  });

  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;

  // update redux state
  useEffect(() => {
    if (columnName && sortDirection) {
      handleSort(columnName, sortDirection);
    }
  }, [columnName, sortDirection]);

  const setColumnOption = (selectedValue) => {
    if (selectedValue) {
      setColumnName(selectedValue?.columnName);
    } else {
      setColumnName(selectedValue);
    }
  };

  const setSortDirectionOption = (selectedValue) => {
    if (selectedValue) {
      setSortDirection(selectedValue?.value);
    } else {
      setSortDirection(selectedValue);
    }
  };

  useEffect(() => {
    if (rowFilters.length > 0 && colFilters.length > 0) {
      const parentInfo = checkParentUserExist(rowFilters);
      let filteredDynamicSortData = [];

      const getLineItem = isEOCampaign
        ? getEOColumnNameIdOrWarnMessage(activeRowFilters)
        : getColumnNameIdOrWarnMessage(activeRowFilters, isSAOrAOCampaign);

      // UM check and EO campaign check

      if (getLineItem || (isEOCampaign && getLineItem?.toString())) {
        colFilters.forEach((ele) => {
          const value = isEOCampaign
            ? getEORowSelectionName(getLineItem)
            : getRowSelectionName(getLineItem);

          if (ele.value === value) {
            const colOptions = JSON.parse(JSON.stringify(ele.options));

            filteredDynamicSortData = [
              ...filteredDynamicSortData,
              ...colOptions,
            ];
          }
        });
      } else {
        // handleCloseSort(); // reset the filters
        colFilters.forEach((ele) => {
          if (parentInfo.includes(ele.label)) {
            const colOptions = JSON.parse(JSON.stringify(ele.options));

            filteredDynamicSortData = [
              ...filteredDynamicSortData,
              ...colOptions,
            ];
          }
        });
      }

      const data = [];

      filteredDynamicSortData.forEach((element) => {
        // remove fixed columns for selected rows
        if (!FIXED_COL_FILTERS.includes(element.value)) {
          const columnLabel = element.label.split('.')[0];

          element.columnName = columnLabel;
          element.label =
            messages[element.label] ||
            localize(intl, getMessageKey(element?.label?.split('.')[0]));
          element.isChecked = false;

          data.push(element);
        }
      });

      setOptions(data);
    } else if (colFilters.length > 0) {
      let filteredDynamicSortData = [];

      colFilters.forEach((ele) => {
        const colOptions = JSON.parse(JSON.stringify(ele.options));

        filteredDynamicSortData = [...filteredDynamicSortData, ...colOptions];
      });
      const data = [];

      filteredDynamicSortData.forEach((element) => {
        // remove fixed columns for selected rows
        if (!FIXED_COL_FILTERS.includes(element.value)) {
          const columnLabel = element.label.split('.')[0];

          element.columnName = columnLabel;
          element.label =
            messages[element.label] ||
            localize(intl, getMessageKey(element?.label?.split('.')[0]));
          element.isChecked = false;

          data.push(element);
        }
      });
      setOptions(data);
    }
  }, [rowFilters, colFilters, appliedRowFilters, groupByOptions]);

  const hideSortDropdown =
    activeRowFilters.length > 0 &&
    ((!isEOCampaign &&
      !getColumnNameIdOrWarnMessage(activeRowFilters, isSAOrAOCampaign)) ||
      (!getEOColumnNameIdOrWarnMessage(activeRowFilters) && isEOCampaign));

  return (
    <div className='DynamicSort-sort'>
      <div className='DynamicSort-sortTriggerWrap'>
        {!hideSortDropdown ? (
          <SortFilter
            filterName={
              messages.CERTS_SORT || localize(intl, getMessageKey('CERTS_SORT'))
            }
            options={options}
            headerTitle={
              messages.CERTS_SORT_HEADER_TITLE ||
              localize(intl, getMessageKey('CERTS_SORT_HEADER_TITLE'))
            }
            placeholder={
              messages.CERTS_SORT_PLACEHOLDER ||
              localize(intl, getMessageKey('CERTS_SORT_PLACEHOLDER'))
            }
            setSelectedColumnOption={setColumnOption}
            setSortDirectionOption={setSortDirectionOption}
            isDisabled={
              activeTabCount === 0 ||
              isLoading ||
              (rowFilters.length > 0 && activeRowFilters.length === 0)
            }
            isInMore={isInMore}
            columnValue={sortByColumn}
            sortValue={sortOrder}
            resetFilter={resetDynamicSort}
            isButtonKindIcon={isButtonKindIcon}
          />
        ) : (
          <Box>
            {isInMore ? (
              <Box className='Dynamic-dropdown'>
                <Box className='DynamicSort-flexContainer'>
                  <Typography
                    kind='body3'
                    className='DynamicSort-popoverDescription'>
                    {isEOCampaign
                      ? l(
                          getEOColumnNameIdOrWarnMessage(activeRowFilters, true)
                        )
                      : l(
                          getColumnNameIdOrWarnMessage(
                            activeRowFilters,
                            isSAOrAOCampaign,
                            true
                          )
                        )}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Menu
                isPortalEnabled
                CustomMenu={
                  <Box className='Dynamic-dropdown'>
                    <Box className='DynamicSort-flexContainer'>
                      <Typography
                        kind='body3'
                        className='DynamicSort-popoverDescription'>
                        {isEOCampaign
                          ? l(
                              getEOColumnNameIdOrWarnMessage(
                                activeRowFilters,
                                true
                              )
                            )
                          : l(
                              getColumnNameIdOrWarnMessage(
                                activeRowFilters,
                                isSAOrAOCampaign,
                                true
                              )
                            )}
                      </Typography>
                    </Box>
                  </Box>
                }
                placement='bottom-end'
                trigger={
                  <ButtonSelect
                    ariaLabel={
                      messages.CERTS_SORT ||
                      localize(intl, getMessageKey('CERTS_SORT'))
                    }
                    kind={isButtonKindIcon ? 'rectangle' : 'icon'}
                    size='medium'
                    prefixIcon={
                      <Icon
                        kind='Sort'
                        ariaLabel={
                          messages.CERTS_SORT ||
                          localize(intl, getMessageKey('CERTS_SORT'))
                        }
                      />
                    }
                    label={
                      messages.CERTS_SORT ||
                      localize(intl, getMessageKey('CERTS_SORT'))
                    }
                    isDisabled={activeTabCount === 0 || isLoading}
                    isVisualCueEnabled={false}
                  />
                }
              />
            )}
          </Box>
        )}
      </div>
    </div>
  );
}

DynamicSort.propTypes = {
  rowFilters: PropTypes.arrayOf(PropTypes.shape({})),
  colFilters: COL_FILTERS_MODEL,
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string),
  handleSort: PropTypes.func,
  resetDynamicSort: PropTypes.func,
  isInMore: PropTypes.bool,
  isButtonKindIcon: PropTypes.bool,
};

DynamicSort.defaultProps = {
  rowFilters: [],
  colFilters: [],
  handleSort: () => {},
  resetDynamicSort: () => {},
  appliedRowFilters: [],
  isInMore: false,
  isButtonKindIcon: false,
};

export default DynamicSort;
